import React from 'react';
import { Box, Title } from '@mantine/core';
import { useAuthStore } from '@/stores/authStore';
import { Icon, ICONS } from '@/common/components/Icon';
import DemoFeatures from '@/features/ai/components/DemoSession/DemoFeatures';
import CreateTodo from '@/features/ai/components/DemoSession/CreateTodo';
// import CreateReport from '@/features/ai/components/DemoSession/CreateReport';
import SearchNote from '@/features/ai/components/DemoSession/SearchNote';
import GetTodo from '@/features/ai/components/DemoSession/GetTodo';
import ChatTextInput from '@/features/ai/chat_window/TextInput';
import { useTranslation } from 'react-i18next';

interface ChatMessage {
    content: string;
    isUser: boolean;
    timestamp: Date;
}

interface ChatRightSectionProps {
    messages: ChatMessage[];
    inputValue: string;
    setInputValue: (value: string) => void;
    handleSendMessage: (latestMessage?: string) => void;
    isLoading?: boolean;
    renderChatInterface?: () => React.ReactNode;
    onMessageReceived?: (message: string) => void;
    selectedFeature: string | null;
    setSelectedFeature: (feature: string | null) => void;
    style?: React.CSSProperties;
}

const ChatRightSection: React.FC<ChatRightSectionProps> = ({ 
    messages, 
    inputValue, 
    setInputValue, 
    handleSendMessage,
    isLoading = false,
    renderChatInterface,
    onMessageReceived,
    selectedFeature,
    setSelectedFeature,
    style
}) => {
    const { t } = useTranslation('ai');
    // 사용자 정보 가져오기
    const { user } = useAuthStore();
    const handleFeatureSelect = (featureId: string) => {
        setSelectedFeature(featureId);
        
        // 기능 선택시 프롬프트 설정 (search-note는 제외하고 SearchNote 컴포넌트에서 처리)
        if (featureId !== 'search-note') {
            const featurePrompts: Record<string, string> = {
                // 'create-report': '지난 주 업무 리포트를 생성해주세요.'
            };
            
            setInputValue(featurePrompts[featureId] || '');
        }
    };

    const handleComponentClose = () => {
        setSelectedFeature(null);
    };
    
    const renderSelectedFeature = () => {
        switch (selectedFeature) {
            case 'get-todo':
                return <GetTodo onClose={handleComponentClose} />;
            case 'create-todo':
                return <CreateTodo onClose={handleComponentClose} />;
            case 'create-report':
                return null; // 보고서 기능 임시 비활성화
                // return <CreateReport 
                //     onClose={handleComponentClose} 
                //     onSendMessage={handleSendMessage}
                //     setInputValue={setInputValue}
                // />;
            case 'search-note':
                return (
                    <SearchNote 
                        onClose={handleComponentClose} 
                        onSendMessage={(message) => {
                            // 메시지 중복 전송 방지: 부모 컴포넌트의 onMessageReceived를 직접 호출하지 않음
                            // setInputValue만 호출하고 실제 메시지 처리는 부모 컴포넌트에서 담당
                            console.log('[ChatRightSection] SearchNote에서 메시지 수신:', message.substring(0, 30));
                            setInputValue(message);
                        }} 
                        setInputValue={setInputValue}
                    />
                );
            default:
                return <DemoFeatures onSelectFeature={handleFeatureSelect} />;
        }
    };
    
    // AI에서 받은 응답을 채팅창에 표시하기 위한 함수
    const onSendMessage = (message: string) => {
        // 빈 함수로 유지 - 메시지 중복 방지
        console.log('[ChatRightSection] onSendMessage 무시됨 (의도적):', message.substring(0, 30));
    };

    return (
        <Box style={{ 
            width: '100%', 
            display: 'flex', 
            borderBottomRightRadius: '16px',
            flexDirection: 'column', 
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            ...style 
        }}>
            {/* 메시지가 있으면 채팅 인터페이스 표시 */}
            <Box style={{ 
                flex: 1, 
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                position: 'relative',
                height: 'calc(100% - 72px)',
            }}>
                {messages.length > 0 && renderChatInterface ? renderChatInterface() : (
                    <Box style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}>
                        {/* 사용자 환영 메시지 - 데모 피처 선택시 숨김 */}
                        {!selectedFeature && (
                            <Box style={{ 
                                width: '100%', 
                                textAlign: 'center',
                                marginTop: '60px',
                            }}>
                                <Box style={{ marginBottom: '-25px' }}>
                                    <Icon name={ICONS.AI.LAIKA_LOGO} size={80} />
                                </Box>
                                <Title order={2} style={{ 
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    letterSpacing: '0%',
                                    color: '#333',
                                    fontWeight: 400
                                }}
                                    dangerouslySetInnerHTML={{ 
                                        __html: t('chat.greeting', { name: user?.name || '' })
                                    }}
                                />
                            </Box>
                        )}
                        <Box style={{ 
                            padding: '0 5px', 
                            width: '100%', 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            marginBottom: '60px'
                        }}>
                            {renderSelectedFeature()}
                        </Box>
                    </Box>
                )}
            </Box>
            
            {/* 오른쪽 아래 모서리에 LAIKA_CORNER 아이콘 추가 */}
            {messages.length === 0 && !selectedFeature && (
                <Box style={{ 
                    position: 'absolute', 
                    right: 0, 
                    bottom: 0, 
                    zIndex: 2,
                    pointerEvents: 'none', // 아이콘 위에서 클릭 이벤트가 발생하지 않도록 설정
                    lineHeight: 0,
                    padding: 0,
                    fontSize: 0
                }}>
                    <Icon name={ICONS.AI.LAIKA_CORNER} size={70} />
                </Box>
            )}
            
            {/* 메시지가 있거나 특정 기능이 선택되었을 때만 입력창 표시 */}
            {(messages.length > 0 || selectedFeature) && (
                <Box style={{ flexShrink: 0, position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 3 }}>
                    <ChatTextInput
                        value={inputValue}
                        onChange={setInputValue}
                        onSend={handleSendMessage}
                        isLoading={isLoading}
                        onMessageReceived={onMessageReceived}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ChatRightSection; 